import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { apiGet, apiPost, isBackendConfigured } from '../utils/backendApi';
import { openFeatureRequest } from '../components/BugReportDialog';

// /features — the feature-request board shared across every organization
// (GET /features, publicView: no emails, no org names). Any member files
// one (the dialog) and votes once per request; the super admin triages and
// ships from /admin/features. Status copy here is what requesters see.
export const FEATURE_STATUS = {
  new: ['Under review', 'info'],
  accepted: ['Accepted', 'info'],
  later: ['Later', ''],
  declined: ['Declined', ''],
  planning: ['Planning', 'info'],
  planned: ['Planned', 'info'],
  building: ['Building', 'info'],
  pr: ['Building', 'info'],
  uat: ['Testing on UAT', 'warn'],
  shipped: ['Shipped — live', 'ok'],
};

export function FeatureStatusChip({ status }) {
  const [label, tone] = FEATURE_STATUS[status] || [status || 'Under review', ''];
  return <span className={`mf-chip${tone ? ` is-${tone}` : ''}`}>{label}</span>;
}

const FILTERS = [
  ['open', 'Open'],
  ['planned', 'Planned'],
  ['shipped', 'Shipped'],
  ['later', 'Later'],
  ['declined', 'Declined'],
  ['mine', 'Mine'],
];
const OPEN = new Set(['new', 'accepted', 'planning', 'planned', 'building', 'pr', 'uat']);
const PLANNED = new Set(['accepted', 'planning', 'planned', 'building', 'pr', 'uat']);

function when(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export default function Features() {
  const { user } = useAuth();
  const member = !!user && !user.isAnonymous;
  const [sort, setSort] = useState('top');
  const [filter, setFilter] = useState('open');
  const [items, setItems] = useState(null);
  const [mine, setMine] = useState(null);
  const [error, setError] = useState('');
  const [busyVote, setBusyVote] = useState('');

  useEffect(() => {
    if (!member) return undefined;
    if (!isBackendConfigured()) { setError('The API is not configured.'); return undefined; }
    let stop = false;
    const pull = () => Promise.all([apiGet(`/features?sort=${sort}`), apiGet('/features/mine')])
      .then(([b, m]) => { if (!stop) { setItems(b.features || []); setMine(new Set((m.features || []).map((f) => f.id))); setError(''); } })
      .catch((err) => { if (!stop) setError(err.message); });
    pull();
    const onFiled = () => setTimeout(pull, 1500);
    window.addEventListener('nv:feature-filed', onFiled);
    return () => { stop = true; window.removeEventListener('nv:feature-filed', onFiled); };
  }, [member, sort]);

  const vote = async (id) => {
    setBusyVote(id);
    try {
      const out = await apiPost(`/features/${id}/vote`, {});
      setItems((prev) => (prev || []).map((f) => (f.id === id ? { ...f, voted: out.voted, voteCount: out.voteCount } : f)));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyVote('');
    }
  };

  const shown = (items || []).filter((f) => {
    if (filter === 'mine') return mine?.has(f.id);
    if (filter === 'open') return OPEN.has(f.status);
    if (filter === 'planned') return PLANNED.has(f.status);
    return f.status === filter;
  });

  return (
    <div className="cs-store">
      <header className="cs-store-hero">
        <h1>Feature board</h1>
        <p>What members across nventr are asking for. Vote for what you need; we build from the top of this list. You get an email when something you asked for or voted for ships.</p>
      </header>
      <section className="cs-store-section cs-support-desk">
        <div className="cs-sw-card">
          <div className="bug-list-head">
            <div className="bug-queue-filters" role="tablist" aria-label="Filter">
              {FILTERS.map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={filter === id} className={filter === id ? 'is-on' : ''} onClick={() => setFilter(id)}>{label}</button>
              ))}
            </div>
            <div className="fr-toolbar">
              <div className="bug-queue-filters" role="tablist" aria-label="Sort">
                <button type="button" role="tab" aria-selected={sort === 'top'} className={sort === 'top' ? 'is-on' : ''} onClick={() => setSort('top')}>Top</button>
                <button type="button" role="tab" aria-selected={sort === 'new'} className={sort === 'new' ? 'is-on' : ''} onClick={() => setSort('new')}>New</button>
              </div>
              <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => openFeatureRequest()}>Request a feature</button>
            </div>
          </div>
          {error && <p className="cs-land-micro" role="alert">{error}</p>}
          {!items && !error && <p className="cs-help">Loading…</p>}
          {items && shown.length === 0 && <p className="cs-help">Nothing here yet. {filter === 'mine' ? 'Request a feature and it shows up on the board.' : <Link to="/support">Need help instead?</Link>}</p>}
          <ul className="cs-support-list">
            {shown.map((f) => (
              <li key={f.id} className="cs-support-row bug-row fr-row">
                <button
                  type="button"
                  className={`fr-vote${f.voted ? ' is-on' : ''}`}
                  aria-pressed={f.voted}
                  aria-label={`${f.voted ? 'Remove your vote for' : 'Vote for'} ${f.title}`}
                  onClick={() => vote(f.id)}
                  disabled={busyVote === f.id}
                >
                  <span aria-hidden="true">▲</span>
                  <strong>{f.voteCount}</strong>
                </button>
                <div className="fr-row-main">
                  <div className="bug-row-top">
                    <strong>{f.title || 'Feature request'}</strong>
                    <FeatureStatusChip status={f.status} />
                  </div>
                  {f.description && <p className="bug-row-desc">{f.description}</p>}
                  {f.problem && <p className="bug-row-desc"><em>Why:</em> {f.problem}</p>}
                  {f.triageNote && (f.status === 'later' || f.status === 'declined') && <p className="bug-row-desc"><em>nventr:</em> {f.triageNote}</p>}
                  <span className="cs-help">
                    {f.requester} · {when(f.createdAt)}
                    {mine?.has(f.id) ? ' · yours' : ''}
                    {f.status === 'shipped' && f.shippedAt ? ` · shipped ${when(f.shippedAt)}` : ''}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
